import { useState, useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const VAPID_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

const PushBanner = () => {
  const [visible, setVisible] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    // Don't show if not supported, already subscribed, or dismissed
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    if (localStorage.getItem('push_subscribed')) return;
    if (sessionStorage.getItem('push_banner_dismissed')) return;

    // Check if already has permission
    if (Notification.permission === 'granted') {
      localStorage.setItem('push_subscribed', 'true');
      return;
    }
    if (Notification.permission === 'denied') return;

    const timer = setTimeout(() => setVisible(true), 4000);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    sessionStorage.setItem('push_banner_dismissed', 'true');
  };

  const handleEnable = async () => {
    setSubscribing(true);
    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/sw-push.js');
      await navigator.serviceWorker.ready;

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notifications blocked. Check your browser settings.');
        setSubscribing(false);
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_KEY),
      });

      // Send subscription to backend
      await api.post('/push/subscribe', { subscription: subscription.toJSON() });

      localStorage.setItem('push_subscribed', 'true');
      toast.success("You're locked in! Breaking alerts coming your way.");
      setVisible(false);
    } catch (error) {
      console.error('Push subscription failed:', error);
      toast.error('Something went wrong. Try again.');
    } finally {
      setSubscribing(false);
    }
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[999] transition-transform duration-500 ease-out"
      style={{ transform: visible ? 'translateY(0)' : 'translateY(100%)' }}
      data-testid="push-banner"
    >
      <div className="max-w-lg mx-auto px-3 pb-3">
        <div className="bg-gradient-to-r from-[#1a1a1f] to-[#111114] border border-yellow-500/30 rounded-2xl shadow-2xl shadow-yellow-500/10 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center animate-pulse">
              <Bell size={18} className="text-yellow-500" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-bold leading-tight">Get Breaking News Alerts</p>
              <p className="text-gray-500 text-[11px]">Be first to know when stories drop</p>
            </div>
            <button
              onClick={handleEnable}
              disabled={subscribing}
              className="shrink-0 px-4 py-2 bg-yellow-500 text-white text-xs font-bold rounded-full hover:bg-yellow-600 transition-colors disabled:opacity-50"
              data-testid="push-banner-enable-btn"
            >
              {subscribing ? '...' : 'Turn On'}
            </button>
            <button
              onClick={handleDismiss}
              className="shrink-0 text-gray-600 hover:text-white transition-colors p-1"
              data-testid="push-banner-dismiss-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushBanner;
